import React from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LayoutDashboard, FolderKanban, Compass, MessageSquare, Sparkles, Hammer, ClipboardCheck, ShieldCheck, Settings, LogOut } from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/projects", label: "Projekte", icon: FolderKanban, testid: "nav-projects" },
  { to: "/discovery", label: "Discovery", icon: Compass, testid: "nav-discovery" },
  { to: "/chat", label: "Chat", icon: MessageSquare, testid: "nav-chat" },
  { to: "/prompts", label: "Prompt Studio", icon: Sparkles, testid: "nav-prompts" },
  { to: "/builds", label: "Builds", icon: Hammer, testid: "nav-builds" },
  { to: "/reviews", label: "Review", icon: ClipboardCheck, testid: "nav-reviews" },
  { to: "/audits", label: "Audit", icon: ShieldCheck, testid: "nav-audits" },
];

export const InternalLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="internal-layout">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-card border-r border-border sticky top-0 h-screen flex flex-col">
        <Link to="/dashboard" className="px-5 pt-6 pb-5 border-b border-border block" data-testid="brand-link">
          <div className="font-display text-xl leading-none text-primary">
            appwerkstatt<span className="text-accent">.dev</span><span className="text-destructive">_</span>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1">Portal · Intern</div>
        </Link>
        <nav className="flex-1 py-4">
          {NAV.map(({ to, label, icon: Icon, testid }) => {
            const active = loc.pathname === to || loc.pathname.startsWith(to + "/");
            return (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={`flex items-center gap-3 mx-2 px-3 py-2.5 text-sm rounded-md transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground font-medium"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.6} />
                {label}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-border p-3 space-y-1">
          <NavLink to="/settings" data-testid="nav-settings" className="flex items-center gap-3 mx-1 px-3 py-2 text-sm rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground">
            <Settings className="w-4 h-4" strokeWidth={1.6} /> Einstellungen
          </NavLink>
        </div>
        <div className="border-t border-border p-4">
          <div className="font-mono text-[11px] text-muted-foreground truncate">{user?.email}</div>
          <div className="text-xs text-foreground mt-1 truncate">{user?.name}</div>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 text-xs px-3 py-1.5 rounded-md border border-border hover:bg-secondary transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Abmelden
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="border-b border-border bg-card px-8 py-3 flex items-center justify-between sticky top-0 z-10">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
            {loc.pathname}
          </div>
          <div className="flex items-center gap-2 text-[11px] font-mono text-muted-foreground">
            <span className="dot" style={{ background: "var(--aw-turquoise)" }}></span>
            SYSTEM HEALTHY
          </div>
        </div>
        <div className="px-8 py-8">{children}</div>
      </main>
    </div>
  );
};
